/**
 * useSignup - A consolidated hook for managing the entire signup flow
 *
 * This hook provides a unified interface for:
 * - Form state management with validation
 * - API integration with loading and error states
 * - Full signup flow functionality
 */
import { useSignup as useSignupContext } from "../SignupContext";
import { usePhoneNumber } from "./fields/usePhoneNumber";
import { useEmail } from "./fields/useEmail";
import { useVerificationCode } from "./fields/useVerificationCode";
import { useCurp } from "./fields/useCurp";
import { useCurpDetails } from "./fields/useCurpDetails";
import { useOccupation } from "./fields/useOccupation";
import { useIncome } from "./fields/useIncome";
import { useAddress } from "./fields/useAddress";
import { usePin } from "./fields/usePin";
import { useReferralData } from "./fields/useReferralData";
import { useSendVerificationCode } from "./api/useSendVerificationCode";
import { useSubmitVerificationCode } from "./api/useSubmitVerificationCode";
import { useValidateCurp } from "./api/useValidateCurp";
import { useSubmitSignup } from "./api/useSubmitSignup";
import { useGenerateCurp } from "./api/useGenerateCurp";
import { useLoginPin } from "./api";
/**
 * The main hook for the signup flow
 * @returns A unified interface for the signup flow with all form fields and API operations
 */
export function useSignup() {
    // Get context with API client
    const { apiClient } = useSignupContext();
    // Initialize all field hooks
    const phoneNumber = usePhoneNumber();
    const email = useEmail();
    const verificationCode = useVerificationCode();
    const curp = useCurp();
    const curpDetails = useCurpDetails();
    const occupation = useOccupation();
    const income = useIncome();
    const address = useAddress();
    const pin = usePin();
    const referralData = useReferralData();
    // Initialize API hooks
    const sendVerificationCode = useSendVerificationCode();
    const submitVerificationCode = useSubmitVerificationCode();
    const validateCurp = useValidateCurp();
    const generateCurp = useGenerateCurp();
    const submitSignup = useSubmitSignup();
    const loginPin = useLoginPin();
    // Create a loading state that combines all API loading states
    const isLoading = sendVerificationCode.isLoading ||
        submitVerificationCode.isLoading ||
        validateCurp.isLoading ||
        generateCurp.isLoading ||
        submitSignup.isLoading ||
        loginPin.isLoading;
    // Create an error state that combines all API error states
    const error = sendVerificationCode.error ||
        submitVerificationCode.error ||
        validateCurp.error ||
        generateCurp.error ||
        submitSignup.error ||
        loginPin.error;
    // Create a reset function that resets all API states
    const resetApiState = () => {
        sendVerificationCode.reset();
        submitVerificationCode.reset();
        validateCurp.reset();
        generateCurp.reset();
        submitSignup.reset();
        loginPin.reset();
    };
    // Return the complete API
    return {
        // API client for direct access if needed
        apiClient,
        // Basic account details
        phoneNumber,
        email,
        verificationCode,
        // CURP and personal information
        curp,
        curpDetails,
        // Employment information
        occupation,
        income,
        // Address information
        address,
        // Security information
        pin,
        // Referral information
        referralData,
        // API operations
        sendVerificationCode,
        submitVerificationCode,
        validateCurp,
        generateCurp,
        submitSignup,
        loginPin,
        // Combined states
        isLoading,
        error,
        resetApiState,
    };
}
